import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, Form, message } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  GlobalOutlined,
  KeyOutlined,
  EditOutlined,
  SafetyCertificateOutlined,
  TagsOutlined,
  UsergroupAddOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import CreateWorkspaceModal from "./InternalConfigurationTable/CreateWorkspaceModal/";
import AddMarketModal from "./InternalConfigurationTable/AddMarketModal/";
import InviteUsersModal from "./InternalConfigurationTable/InviteUsersModal";
import CreateUserModal from "./InternalConfigurationTable/CreateUserModal";
import UserCreatedResultModal from "./InternalConfigurationTable/UserCreatedResultModal";
import CreateUserResultModal from "./InternalConfigurationTable/CreateUserResultModal";
import DeleteUserModal from "./InternalConfigurationTable/DeleteUserModal";
import DeleteWorkspaceModal from "./InternalConfigurationTable/DeleteWorkspaceModal";
import ManageAllowedDomainsModal from "./InternalConfigurationTable/ManageAllowedDomainsModal";
import ManageComboNamesModal from "./InternalConfigurationTable/ManageComboNamesModal";
import ManageKeyAccountSkusModal from "./InternalConfigurationTable/ManageKeyAccountSkusModal";
import { ResetPasswordChoiceModal } from "@web/components/UserManagementPage/ResetPasswordChoiceModal";
import { ResetPasswordResultModal } from "@web/components/UserManagementPage/ResetPasswordResultModal";
import { fetchAllWorkspacesAction } from "../slices/workspaceSlice";
import {
  inviteUsersToWorkspaces,
  createUserByEmail,
  buildUserClientPayload,
  updateUserInfo,
  getDeletionJobStatus,
} from "../api/internal_tool_endpoints";
import { resetUserPassword, reactivateUser } from "../api/userManagementApi";
import { usePipelineJobPoll } from "../hooks/usePipelineJobPoll";

/**
 * Workspace & User provisioning actions — the button bar that opens the
 * create/invite/delete/reset modals. Self-contained: it reads the workspace
 * list from redux and dispatches its own fetch, so it can be dropped onto any
 * admin surface (it lives on the portal homepage) without prop wiring.
 */
export default function WorkspaceUserActions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state — the allowed-domains and combo-names pickers need the
  // workspace list.
  const { allWorkspaces } = useSelector((state) => state.workspace);
  const workspaceIds = useMemo(
    () =>
      [
        ...new Set(
          (allWorkspaces || []).map((w) => w.id_client).filter(Boolean),
        ),
      ].sort(),
    [allWorkspaces],
  );

  // Local state
  const [isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen] =
    useState(false);
  const [isAddMarketModalOpen, setIsAddMarketModalOpen] = useState(false);
  const [isInviteUsersModalOpen, setIsInviteUsersModalOpen] = useState(false);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  // When Create hits a deactivated email (409), keep the modal open and
  // offer to reactivate + apply the entered scope instead of a dead-end error.
  // Holds the deactivated email (null = no offer); the scope to apply is read
  // fresh from the modal at reactivate time, so post-409 edits aren't stale.
  const [reactivatePrompt, setReactivatePrompt] = useState(null);
  const [reactivating, setReactivating] = useState(false);
  const [isUserCreatedResultModalOpen, setIsUserCreatedResultModalOpen] =
    useState(false);
  const [isCreateUserResultModalOpen, setIsCreateUserResultModalOpen] =
    useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    useState(false);
  const [isResetPasswordResultModalOpen, setIsResetPasswordResultModalOpen] =
    useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [isDeleteWorkspaceModalOpen, setIsDeleteWorkspaceModalOpen] =
    useState(false);
  const [createdUsers, setCreatedUsers] = useState([]);
  const [createdUser, setCreatedUser] = useState(null);
  // Invite-domain allowlist manager (a workspace picker + chip editor). Kept as
  // a single action-bar modal that reads live on open, so a redux-persist cache
  // can't serve a stale list after another admin edits it.
  const [allowedDomainsOpen, setAllowedDomainsOpen] = useState(false);
  // Combo names editor: a workspace picker plus the names every dashboard
  // gives its combo splits, read live when a workspace is picked.
  const [comboNamesOpen, setComboNamesOpen] = useState(false);
  // Key Accounts SKU groups editor, read live when a workspace is picked.
  const [skuGroupsOpen, setSkuGroupsOpen] = useState(false);

  // Form instances
  const [inviteUsersForm] = Form.useForm();
  const [createUserForm] = Form.useForm();

  // Active workspace-deletion poll timers, cleared on unmount.
  const deletionTimersRef = useRef(new Set());
  // Create Workspace and Add Market share one pipeline-status poll.
  const pollPipelineJob = usePipelineJobPoll();

  // Fetch workspaces on component mount
  useEffect(() => {
    // The new API fetches all workspaces for all clients, no need for id_client parameter
    dispatch(fetchAllWorkspacesAction());
  }, [dispatch]);

  // Stop any in-flight deletion polls when the component unmounts (the pipeline
  // poll's own cleanup lives in usePipelineJobPoll).
  useEffect(() => {
    const deletionTimers = deletionTimersRef.current;
    return () => {
      deletionTimers.forEach((t) => clearInterval(t));
      deletionTimers.clear();
    };
  }, []);

  const handleCreateWorkspace = () => setIsCreateWorkspaceModalOpen(true);
  const handleWorkspaceModalClose = () => setIsCreateWorkspaceModalOpen(false);
  const handleWorkspaceCreated = (result) => {
    setIsCreateWorkspaceModalOpen(false);
    // Refresh workspaces data after successful creation
    dispatch(fetchAllWorkspacesAction());
    // The workspace's first version lands is_active=false/status="pending" and
    // only becomes usable once the pipeline reports back, so surface the run
    // rather than letting the success toast imply the work is done.
    const idClient = result?.client;
    const versionId = result?.new_version;
    if (!idClient || !versionId) return;
    pollPipelineJob(idClient, versionId, (result?.countries || []).join(", "));
  };

  const handleAddMarket = () => setIsAddMarketModalOpen(true);
  const handleAddMarketModalClose = () => setIsAddMarketModalOpen(false);
  const handleMarketAdded = (result) => {
    setIsAddMarketModalOpen(false);
    // Refresh workspaces data so the new market appears under the workspace
    dispatch(fetchAllWorkspacesAction());
    // The market only exists in the data once the pipeline has rebuilt it, so
    // keep the same running/finished indicator an edit gets rather than
    // implying the work is done when the API returns.
    const idClient = result?.client;
    const versionId = result?.new_version;
    if (!idClient || !versionId) return;
    pollPipelineJob(idClient, versionId, (result?.countries || []).join(", "));
  };

  const handleInviteUsers = () => setIsInviteUsersModalOpen(true);
  const handleInviteUsersModalClose = () => setIsInviteUsersModalOpen(false);
  const handleCreateUser = () => setIsCreateUserModalOpen(true);
  const handleCreateUserModalClose = () => {
    setIsCreateUserModalOpen(false);
    setReactivatePrompt(null);
  };
  const handleManageUserScope = () => navigate("/user-scope-management");
  const handleDeleteUser = () => setIsDeleteUserModalOpen(true);
  const handleDeleteUserModalClose = () => setIsDeleteUserModalOpen(false);
  const handleUserDeleted = (_result) => {
    setIsDeleteUserModalOpen(false);
    message.success("User has been permanently deleted from the system");
  };
  const handleResetPassword = () => setIsResetPasswordModalOpen(true);
  const handleResetPasswordModalClose = () =>
    setIsResetPasswordModalOpen(false);
  const handleConfirmResetPassword = async (email, delivery) => {
    setIsResetting(true);
    try {
      const result = await resetUserPassword(email, delivery);
      setResetPasswordResult({ email, delivery, ...result });
      setIsResetPasswordModalOpen(false);
      setIsResetPasswordResultModalOpen(true);
    } catch (error) {
      message.error(error.message || "Failed to reset password.");
    } finally {
      setIsResetting(false);
    }
  };
  const handleDeleteWorkspace = () => setIsDeleteWorkspaceModalOpen(true);
  const handleDeleteWorkspaceModalClose = () =>
    setIsDeleteWorkspaceModalOpen(false);

  // Poll an async deletion job until DONE/FAILED. The workspace keeps showing in
  // the list (it's only removed on a refetch) until the background ClickHouse
  // purge completes, matching the "drops only when DONE" behavior.
  const DELETION_POLL_INTERVAL_MS = 5000;
  const DELETION_MAX_POLL_ATTEMPTS = 240; // ~20 min safety cap
  // The retire runs on Databricks, so its run page is the only place to see
  // progress or why it failed. The 202 and the poll both carry the run URL once
  // the trigger records it. A job document can carry a run id with no URL, so
  // the UI omits the link rather than faking one.
  //
  // The run page is titled after the *job*, which is one shared multi-mode
  // pipeline ("Client Data Creator") that both builds and retires a workspace
  // depending on its `mode` parameter. So the run legitimately reads as a
  // creator run even though `mode=retire`. Name the link for the run's purpose
  // and say so on hover, or the operator reasonably concludes they opened the
  // wrong thing.
  const RETIRE_RUN_HINT =
    "Runs on the shared client-data pipeline job with mode=retire — the run " +
    "page is titled after that job, so it reads as a creator run. Check its " +
    "mode and id_client parameters to confirm you have the right run.";

  const withJobLink = (text, runUrl, label) =>
    runUrl ? (
      <span>
        {`${text} `}
        <a
          href={runUrl}
          target="_blank"
          rel="noreferrer"
          title={RETIRE_RUN_HINT}
        >
          {label}
        </a>
      </span>
    ) : (
      text
    );

  // A workspace whose ClickHouse step is NOT_APPLICABLE was deleted on staging,
  // which shares production's database — its rows are still there and the
  // production delete is what clears them. Saying only "deleted" would read as
  // if the whole retire had run.
  const deletionDoneText = (idClient, steps) =>
    steps?.clickhouse_purge === "NOT_APPLICABLE"
      ? `Workspace "${idClient}" removed from staging Firestore. ClickHouse was not purged — staging shares production's database, so run the production delete to clear those rows.`
      : `Workspace "${idClient}" deleted.`;

  const pollDeletionJob = (jobId, idClient, hide) => {
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const job = await getDeletionJobStatus(jobId);
        if (job.status === "DONE") {
          clearInterval(timer);
          deletionTimersRef.current.delete(timer);
          hide();
          message.success(deletionDoneText(idClient, job.steps));
          dispatch(fetchAllWorkspacesAction());
        } else if (job.status === "FAILED") {
          clearInterval(timer);
          deletionTimersRef.current.delete(timer);
          hide();
          // A failure is exactly when the run's logs are wanted, so link them.
          message.error(
            withJobLink(
              `Deletion failed for "${idClient}": ${job.error_message || "unknown error"}`,
              job.run_url,
              "View logs",
            ),
          );
          dispatch(fetchAllWorkspacesAction());
        } else if (attempts >= DELETION_MAX_POLL_ATTEMPTS) {
          clearInterval(timer);
          deletionTimersRef.current.delete(timer);
          hide();
          message.warning(
            withJobLink(
              `Still deleting "${idClient}". Check the Refresh Monitor later to confirm.`,
              job.run_url,
              "View retire run",
            ),
          );
        }
      } catch (e) {
        // Transient poll error (network / token refresh) — keep polling.
        console.warn("delete-job poll error:", e);
      }
    }, DELETION_POLL_INTERVAL_MS);
    deletionTimersRef.current.add(timer);
  };

  const handleWorkspaceDeleted = (result) => {
    setIsDeleteWorkspaceModalOpen(false);
    const jobId = result?.jobId;
    const idClient = result?.id_client;
    if (!jobId || !idClient) {
      // No job to track (unexpected) — just refresh the list.
      dispatch(fetchAllWorkspacesAction());
      return;
    }
    // A staging delete comes back already terminal: there is no ClickHouse
    // retire to wait for, so polling it would only re-read a finished job and
    // report a bare "deleted" without saying what was skipped.
    if (result.status === "DONE") {
      message.success(
        result.message || deletionDoneText(idClient, result.steps),
      );
      dispatch(fetchAllWorkspacesAction());
      return;
    }
    // Persistent progress indicator (closed when the job reaches a terminal state).
    const hide = message.loading(
      withJobLink(
        `Deleting "${idClient}" in the background...`,
        result?.run_url,
        "View retire run",
      ),
      0,
    );
    pollDeletionJob(jobId, idClient, hide);
  };
  const handleUsersInvited = async (data) => {
    // Show loading message
    const hide = message.loading("Sending invitations...", 0);

    try {
      // Call the API to send invitations
      const results = await inviteUsersToWorkspaces({
        emails: data.emails,
        workspaceAssignments: data.workspaceAssignments,
        accountType: data.accountType,
        defaultWorkspaceId: data.defaultWorkspaceId,
      });

      // Hide loading message
      hide();

      setIsInviteUsersModalOpen(false);

      // Extract user data from results and show in modal
      const usersData = results.map((r) => r.result);
      setCreatedUsers(usersData);
      setIsUserCreatedResultModalOpen(true);

      // Show success message
      message.success(
        `${usersData.length} user${
          usersData.length > 1 ? "s" : ""
        } invited successfully! They will receive an email to set up their password.`,
      );
    } catch (error) {
      console.error("Failed to invite users:", error);

      // Hide loading message
      hide();

      // Surface the backend's reason (e.g. "product_scopes must include at
      // least one country …") instead of a generic message.
      message.error(
        error?.message || "Failed to invite users. Please try again.",
      );
    }
  };

  const handleUserCreated = async (data) => {
    // Each attempt starts fresh — clear any prior reactivate offer.
    setReactivatePrompt(null);
    const hide = message.loading("Creating user...", 0);

    try {
      const result = await createUserByEmail({
        email: data.email,
        name: data.name,
        workspaceAssignments: data.workspaceAssignments,
        accountType: data.accountType,
        defaultWorkspaceId: data.defaultWorkspaceId,
      });

      hide();
      console.log("✅ User created successfully:", result);

      setCreatedUser(result);
      setIsCreateUserModalOpen(false);
      setIsCreateUserResultModalOpen(true);
      createUserForm.resetFields();
      message.success("User created successfully!");
    } catch (error) {
      hide();
      console.error("❌ Error creating user:", error);
      // 409 = the email belongs to a deactivated user. Keep the modal open and
      // offer to reactivate + apply the entered scope, rather than a
      // dead-end error toast.
      if (error.status === 409) {
        setReactivatePrompt(data.email);
        return;
      }
      message.error(
        `Failed to create user: ${error.message || "Unknown error"}`,
      );
    }
  };

  // Reactivate the deactivated user AND apply the scope the admin just entered
  // (recovery matches intent, not stale prior access). Two calls: reactivate,
  // then the scope-replacing update. A failure after reactivation is surfaced
  // distinctly so the admin knows the account is back but scope needs a manual
  // edit.
  const handleReactivateFromCreate = async (formData) => {
    const email = formData?.email;
    if (!email) return;
    setReactivating(true);
    const hide = message.loading("Reactivating user...", 0);
    try {
      await reactivateUser(email);
    } catch (error) {
      hide();
      setReactivating(false);
      message.error(
        `Failed to reactivate ${email}: ${error.message || "Unknown error"}`,
      );
      return;
    }
    // Account is back; now replace its scope with what was configured.
    try {
      const payload = buildUserClientPayload(formData);
      await updateUserInfo({
        email,
        user_info: {
          email,
          default_id_client: payload.default_id_client,
          id_client: payload.id_client,
        },
      });
      hide();
      message.success(`${email} reactivated with the configured access.`);
    } catch (error) {
      hide();
      message.warning(
        `${email} was reactivated, but applying the new access failed: ${
          error.message || "unknown error"
        }. Edit their scope in User Scope Management.`,
      );
    } finally {
      setReactivating(false);
      setReactivatePrompt(null);
      setIsCreateUserModalOpen(false);
      createUserForm.resetFields();
    }
  };

  const actionButtonStyle = {
    height: "2.25rem",
    padding: "0.5rem 0.9rem",
    borderRadius: "8px",
    fontSize: "0.8125rem",
  };

  return (
    <>
      <section className="mb-10 border-b border-[#0000000f] pb-8">
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-[#00000073]">
          Quick actions
        </h2>
        <div className="flex flex-col gap-4">
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#00000073]">
              Workspace
            </div>
            <div className="flex flex-wrap gap-2.5">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateWorkspace}
                style={actionButtonStyle}
              >
                Create Workspace
              </Button>
              <Button
                type="primary"
                icon={<GlobalOutlined />}
                onClick={handleAddMarket}
                style={actionButtonStyle}
              >
                Add Market
              </Button>
              <Button
                icon={<SafetyCertificateOutlined />}
                onClick={() => setAllowedDomainsOpen(true)}
                style={actionButtonStyle}
              >
                Allowed Domains
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteWorkspace}
                style={actionButtonStyle}
              >
                Delete Workspace
              </Button>
            </div>
          </div>
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#00000073]">
              User
            </div>
            <div className="flex flex-wrap gap-2.5">
              <Button
                type="primary"
                icon={<UsergroupAddOutlined />}
                onClick={handleInviteUsers}
                style={actionButtonStyle}
              >
                Invite Users
              </Button>
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={handleCreateUser}
                style={actionButtonStyle}
              >
                Create User
              </Button>
              <Button
                icon={<KeyOutlined />}
                onClick={handleResetPassword}
                style={actionButtonStyle}
              >
                Reset Password
              </Button>
              <Button
                icon={<EditOutlined />}
                onClick={handleManageUserScope}
                style={actionButtonStyle}
              >
                Manage User Scope
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteUser}
                style={actionButtonStyle}
              >
                Delete User
              </Button>
            </div>
          </div>
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#00000073]">
              Dashboard settings
            </div>
            <div className="flex flex-wrap gap-2.5">
              <Button
                icon={<TagsOutlined />}
                onClick={() => setComboNamesOpen(true)}
                style={actionButtonStyle}
              >
                Combo Names
              </Button>
              <Button
                icon={<TagsOutlined />}
                onClick={() => setSkuGroupsOpen(true)}
                style={actionButtonStyle}
              >
                SKU Groups
              </Button>
            </div>
          </div>
        </div>
      </section>

      <CreateWorkspaceModal
        open={isCreateWorkspaceModalOpen}
        onCancel={handleWorkspaceModalClose}
        onSuccess={handleWorkspaceCreated}
      />

      <AddMarketModal
        open={isAddMarketModalOpen}
        onCancel={handleAddMarketModalClose}
        onSuccess={handleMarketAdded}
      />

      <InviteUsersModal
        open={isInviteUsersModalOpen}
        onCancel={handleInviteUsersModalClose}
        onOk={handleUsersInvited}
        form={inviteUsersForm}
      />

      <CreateUserModal
        open={isCreateUserModalOpen}
        onCancel={handleCreateUserModalClose}
        onOk={handleUserCreated}
        form={createUserForm}
        reactivateEmail={reactivatePrompt}
        reactivating={reactivating}
        onReactivate={handleReactivateFromCreate}
      />

      <UserCreatedResultModal
        open={isUserCreatedResultModalOpen}
        onClose={() => setIsUserCreatedResultModalOpen(false)}
        users={createdUsers}
      />

      <CreateUserResultModal
        open={isCreateUserResultModalOpen}
        onClose={() => setIsCreateUserResultModalOpen(false)}
        user={createdUser}
      />

      <DeleteUserModal
        open={isDeleteUserModalOpen}
        onClose={handleDeleteUserModalClose}
        onSuccess={handleUserDeleted}
      />

      <ResetPasswordChoiceModal
        open={isResetPasswordModalOpen}
        isResetting={isResetting}
        onConfirm={handleConfirmResetPassword}
        onCancel={handleResetPasswordModalClose}
      />

      <ResetPasswordResultModal
        open={isResetPasswordResultModalOpen}
        result={resetPasswordResult}
        onClose={() => {
          setIsResetPasswordResultModalOpen(false);
          setResetPasswordResult(null);
        }}
      />

      <DeleteWorkspaceModal
        open={isDeleteWorkspaceModalOpen}
        onClose={handleDeleteWorkspaceModalClose}
        onSuccess={handleWorkspaceDeleted}
      />

      <ManageAllowedDomainsModal
        open={allowedDomainsOpen}
        workspaces={workspaceIds}
        onCancel={() => setAllowedDomainsOpen(false)}
      />

      <ManageComboNamesModal
        open={comboNamesOpen}
        workspaces={workspaceIds}
        onCancel={() => setComboNamesOpen(false)}
      />

      <ManageKeyAccountSkusModal
        open={skuGroupsOpen}
        workspaces={workspaceIds}
        onCancel={() => setSkuGroupsOpen(false)}
      />
    </>
  );
}
